import * as React from 'react';
import classNames from 'classnames';
import Icon from '../Icon';

export type PaginationProps = {
  pageNumber: number;
  pageSize: number;
  totalResultSize: number;
  className?: string;
  onPageChange: (nextPageNumber: number) => void;
};

// Amount of pages surrounding the active page
const PAGE_MARGINS = 2;

const Pagination: React.FC<PaginationProps> = ({
  pageNumber,
  pageSize,
  totalResultSize,
  className,
  onPageChange,
}) => {
  const numberOfPages = React.useMemo(
    () => Math.ceil(totalResultSize / pageSize),
    [totalResultSize, pageSize],
  );

  const handlePreviousClick = React.useCallback(
    () => onPageChange(Math.max(pageNumber - 1, 1)),
    [pageNumber, numberOfPages]
  );

  const handleNextClick = React.useCallback(
    () => onPageChange(Math.min(pageNumber + 1, numberOfPages)),
    [pageNumber, numberOfPages]
  );

  const pageButtons: React.ReactNode[] = React.useMemo(() => {
    const elements = [];

    let min = Math.max(pageNumber - PAGE_MARGINS, 1);
    let max = Math.min(pageNumber + PAGE_MARGINS, numberOfPages);

    const visiblePages = Math.min(PAGE_MARGINS * 2 + 1, numberOfPages);
    const pages = (max - min) + 1;

    if (pages < visiblePages) {
      const delta = visiblePages - pages;
      if (max < visiblePages) {
        max = Math.min(max + delta, numberOfPages)
      } else if (min > (max - visiblePages)) {
        min = Math.max(min - delta, 1)
      }
    }

    for (let buttonPageNumber = min; buttonPageNumber <= max; buttonPageNumber += 1) {
      const isActive = buttonPageNumber === pageNumber;
      const onClick = onPageChange.bind(null, buttonPageNumber);

      if (isActive) {
        elements.push(
          <div
            key={buttonPageNumber}
            className="transition-all font-semibold text-center cursor-default py-2 w-10 mx-1 rounded-md has-background-blue has-text-white"
          >
            {buttonPageNumber}
          </div>
        );
      } else {
        elements.push(
          <button
            key={buttonPageNumber}
            onClick={onClick}
            className="transition-all font-semibold text-gray-600 cursor-pointer py-2 w-10 mx-1 rounded-md hover:bg-gray-200 hover:text-gray-900"
          >
            {buttonPageNumber}
          </button>
        );
      }
    }

    return elements;
  }, [pageNumber, pageNumber, totalResultSize]);

  return (
    <div
      className={classNames(
        className,
        'flex items-center justify-center'
      )}
    >
      <button
        onClick={handlePreviousClick}
        className={classNames(
          'w-10 flex justify-center items-center cursor-pointer',
          pageNumber < 2 && 'opacity-50 cursor-default'
        )}
        disabled={pageNumber < 2}
      >
        <Icon name="chevron-left" />
      </button>
      {pageButtons}
      <button
        onClick={handleNextClick}
        disabled={pageNumber >= numberOfPages}
        className={classNames(
          'w-10 flex justify-center items-center cursor-pointer',
          pageNumber >= numberOfPages && 'opacity-50 cursor-default'
        )}
      >
        <Icon name="chevron-right" />
      </button>
    </div>
  );
};

export default Pagination;



// WEBPACK FOOTER //
// ./src/components/Pagination/index.tsx